import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, m as motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calculator, FileText, Search, Users } from 'lucide-react';
import { NAV_ITEMS } from '@/components/layout/navigation';
import { documentsService, type DocumentDetail } from '@/services/documents';
import { clientsService, type ClientSummary } from '@/services/clients';
import { returnsService, type ReturnSummary } from '@/services/returns';
import { tolerate } from '@/services/loadError';
import { useDocumentDrawer, usePalette } from '@/stores/ui';
import { useDialogA11y } from '@/lib/useDialogA11y';
import { cn } from '@/lib/cn';
import { useDebounced } from '@/lib/useDebounced';

interface Entry {
  id: string;
  label: string;
  group: 'navigation' | 'documents' | 'clients' | 'returns';
  icon: typeof Search;
  run: () => void;
}

/** ⌘K command palette — universal shortcut: navigate anywhere, find any document. */
export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open, setOpen } = usePalette();
  const [query, setQuery] = useState('');
  const [docs, setDocs] = useState<DocumentDetail[]>([]);
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [returns, setReturns] = useState<ReturnSummary[]>([]);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  // Trampa de foco + devolución al disparador (Escape ya lo maneja el atajo global).
  const dialogRef = useDialogA11y(open, () => setOpen(false));

  // Global shortcut
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!usePalette.getState().open);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  // Reset + focus on open; live document search (fixtures, Fase A)
  useEffect(() => {
    if (open) {
      setQuery('');
      setDocs([]);
      setClients([]);
      setReturns([]);
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // 2-nov (react-best-practices): la búsqueda espera a que se deje de teclear
  // (250 ms); antes cada tecla lanzaba 3 consultas.
  const debounced = useDebounced(query, 250);
  useEffect(() => {
    let alive = true;
    // Búsqueda = dato secundario: si una fuente falla, se muestra lo que haya.
    void tolerate(documentsService.search(debounced), []).then((r) => alive && setDocs(r));
    const q = debounced.trim();
    if (q.length >= 2) {
      void tolerate(clientsService.list(q), []).then((r) => alive && setClients(r.slice(0, 4)));
      void tolerate(returnsService.search(q), []).then((r) => alive && setReturns(r.slice(0, 4)));
    } else {
      setClients([]);
      setReturns([]);
    }
    return () => {
      alive = false;
    };
  }, [debounced]);

  const entries = useMemo<Entry[]>(() => {
    const q = query.trim().toLowerCase();
    const nav = NAV_ITEMS.map<Entry>((item) => ({
      id: item.to,
      label: t(item.labelKey),
      group: 'navigation',
      icon: item.icon,
      run: () => navigate(item.to),
    })).filter((e) => !q || e.label.toLowerCase().includes(q));
    const clientEntries = clients.map<Entry>((c) => ({
      id: `cli-${c.id}`,
      label: `${c.name} · ${c.email}`,
      group: 'clients',
      icon: Users,
      run: () => navigate(`/clients/${c.id}`),
    }));
    const returnEntries = returns.map<Entry>((r) => ({
      id: `ret-${r.id}`,
      label: `${r.client} · ${r.year} · ${t(`status.${r.status}`, r.status)}`,
      group: 'returns',
      icon: Calculator,
      run: () => navigate(`/tax-returns/${r.id}`),
    }));
    const documents = docs.map<Entry>((d) => ({
      id: `doc-${d.id}`,
      label: `${d.title} · ${d.client}`,
      group: 'documents',
      icon: FileText,
      run: () => useDocumentDrawer.getState().open(d.id),
    }));
    return [...clientEntries, ...returnEntries, ...documents, ...nav.slice(0, q ? 8 : 6)];
  }, [query, docs, clients, returns, navigate, t]);

  useEffect(() => setSelected(0), [entries.length]);

  const runEntry = (entry: Entry) => {
    setOpen(false);
    entry.run();
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, entries.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter' && entries[selected]) {
      runEntry(entries[selected]);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center px-4 pt-[12vh]" role="dialog" aria-modal="true">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-background/60 backdrop-blur-sm"
          />
          <motion.div
            ref={dialogRef}
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            className="glass-strong relative w-full max-w-xl overflow-hidden rounded-2xl"
          >
            <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3.5">
              <Search className="h-4 w-4 flex-none text-muted-foreground" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKey}
                placeholder={t('palette.placeholder')}
                aria-label={t('palette.placeholder')}
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
              <kbd className="rounded-md border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">esc</kbd>
            </div>
            <div className="max-h-80 overflow-y-auto p-2">
              {entries.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">{t('palette.noResults')}</p>
              )}
              {entries.map((entry, i) => {
                const showHeader = entry.group !== entries[i - 1]?.group;
                return (
                  <div key={entry.id}>
                    {showHeader && (
                      <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        {t(`palette.${entry.group}`)}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => runEntry(entry)}
                      onMouseEnter={() => setSelected(i)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors',
                        i === selected ? 'bg-primary/15 text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      <entry.icon className="h-4 w-4 flex-none" />
                      <span className="truncate">{entry.label}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
